import React, { Fragment, useMemo, useRef } from 'react';
import { Affix, Empty } from 'antd';
import BodyRow from '../BodyRow';
import CheckboxCell from '../CheckboxCell';
import { useHeaderSize } from '../../services/headerSizeCalculator';
import ExpandedContainer from '../ExpandedContainer';
import { useValueRef } from '../../../../../../../hooks/useValueRef';
import { useDisplayedData } from '../../services/dataPreparation';
import { useStateSelector } from '../../stateManager';
import { createIsSelectedRowSelector } from '../../utils/selectors';
import { useRowSelectionHandlers } from '../../services/rowSelection';
import { useResizeObserverState } from '../../../../../../../hooks/useResizeObserverState';
import styles from './index.module.scss';

const Body = ({
  grouped,
  columns,
  withCheckbox,
  rowKey,
  renderExpanded,
  groupFn,
  hasData,
}) => {
  const displayItems = useDisplayedData();

  const ref = useRef();

  return (
    <div className={styles.root} ref={ref}>
      {displayItems?.length ? (
        <Content
          grouped={grouped}
          columns={columns}
          withCheckbox={withCheckbox}
          rowKey={rowKey}
          renderExpanded={renderExpanded}
          groupFn={groupFn}
          data={displayItems}
        />
      ) : (
        <EmptyContent rootRef={ref} hasData={hasData} />
      )}
    </div>
  );
};

export default Body;

function EmptyContent({ rootRef, hasData }) {
  const width = useResizeObserverState(rootRef, {
    compute: (el) => el?.parentElement?.clientWidth,
    defaultValue: '100%',
  });

  return (
    <BodyRow>
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={hasData ? 'Ничего не найдено' : 'Нет данных'}
        style={{ width }}
        className={styles.empty}
      />
    </BodyRow>
  );
}

function Content({
  grouped,
  columns,
  withCheckbox,
  rowKey,
  renderExpanded,
  groupFn,
  data,
}) {
  return grouped ? (
    <GroupedList
      data={data}
      columns={columns}
      withCheckbox={withCheckbox}
      rowKey={rowKey}
      renderExpanded={renderExpanded}
      groupFn={groupFn}
    />
  ) : (
    <BaseList
      data={data}
      columns={columns}
      withCheckbox={withCheckbox}
      rowKey={rowKey}
      renderExpanded={renderExpanded}
    />
  );
}

function GroupedList({
  data,
  columns,
  withCheckbox,
  rowKey,
  renderExpanded,
  groupFn,
}) {
  const groupFnRef = useValueRef(groupFn);

  const groupedData = useMemo(
    () => groupFnRef.current(data),
    [data, groupFnRef]
  );

  return groupedData.map(({ name, rooms }) => (
    <Fragment key={name}>
      <GroupTitle name={name} />
      <BaseList
        data={rooms}
        columns={columns}
        withCheckbox={withCheckbox}
        rowKey={rowKey}
        renderExpanded={renderExpanded}
      />
    </Fragment>
  ));
}

function BaseList({ data, columns, withCheckbox, rowKey, renderExpanded }) {
  const colOffset = withCheckbox ? 2 : 1;

  return data.map((record) => {
    const expandedContent =
      typeof renderExpanded === 'function'
        ? renderExpanded({ record, columns, withCheckbox })
        : null;

    return (
      <BodyRow key={record[rowKey]}>
        {withCheckbox && <CheckboxBlock record={record} rowKey={rowKey} />}

        {columns.map(({ Cell, key }, colIndex) => (
          <Cell key={key} record={record} column={colIndex + colOffset} />
        ))}

        {expandedContent ? (
          <ExpandedContainer rowId={record[rowKey]}>
            {expandedContent}
          </ExpandedContainer>
        ) : null}
      </BodyRow>
    );
  });
}

function GroupTitle({ name }) {
  const headerHeight = useHeaderSize();

  return (
    <Affix offsetTop={headerHeight}>
      <div className={styles.titleWrapper}>
        <h4 className={styles.title}>{name}</h4>
      </div>
    </Affix>
  );
}

function CheckboxBlock({ record, rowKey }) {
  const selectIsSelected = createIsSelectedRowSelector(record[rowKey]);
  const selected = useStateSelector(selectIsSelected);

  const { onSelect } = useRowSelectionHandlers();

  const handleClick = () => {
    onSelect({ key: record[rowKey], selected: !selected });
  };

  return (
    <CheckboxCell placement="body" checked={selected} onClick={handleClick} />
  );
}
